/**
 * Catalog-driven module discovery for notification preferences.
 *
 * The list of modules that have a notification event catalog is derived
 * dynamically from `notification_events.config.json` (the single source of
 * truth shared with the backend), NOT a hardcoded list. Adding a module's
 * catalog to that config automatically surfaces it in the central
 * all-modules preferences matrix — no frontend change required.
 *
 * Note: the catalog module key is the Python module folder name (e.g.
 * `ai_agents`), which is the same key used in `modules_entities.config.json`
 * — but NOT always the same as the frontend `ModuleConfig.id` (e.g. ai_agents
 * has id `ai`). So we resolve the display name + icon by the catalog key
 * directly against the entities config, rather than via `getModuleById`.
 */
import notificationEventsConfig from '../../shared-config/notification_events.config.json';
import modulesEntitiesConfig from '../../shared-config/modules_entities.config.json';

/**
 * Who sees a module's events in the central preferences matrix. Mirrors the
 * backend `ModuleCatalog.visibility` (notification_events.config.json). The
 * delivery audience is decided at the emit site; this only gates the matrix
 * render so a tenant user isn't shown sysadmin/admin-only event toggles they
 * could never receive. See docs/platform/push-notifications-design.md.
 */
export type NotificationCatalogVisibility = 'all' | 'tenant_admin' | 'sysadmin';

interface RawCatalogModule {
  visibility?: NotificationCatalogVisibility;
}

interface NotificationEventsConfig {
  modules?: Record<string, RawCatalogModule>;
}

interface RawModuleInfo {
  displayNameKey?: string;
  icon?: string;
}

interface ModulesEntitiesConfig {
  modules?: Record<string, RawModuleInfo>;
}

export interface NotificationCatalogModule {
  /** Catalog key === Python module folder name (e.g. `ai_agents`). */
  id: string;
  /** i18n key for the module display name (in the `common` namespace). */
  labelKey: string;
  /** Icon name from config (resolve via `getIcon`), if any. */
  iconName?: string;
  /** Audience that may see this module's events (default `all`). */
  visibility: NotificationCatalogVisibility;
}

const rawModules =
  (modulesEntitiesConfig as ModulesEntitiesConfig).modules ?? {};

const catalogModules =
  (notificationEventsConfig as NotificationEventsConfig).modules ?? {};

/** Module ids (in config order) that declare a notification event catalog. */
export const NOTIFICATION_CATALOG_MODULE_IDS: string[] = Object.keys(catalogModules);

/** Catalog modules with their resolved display name key + icon name + visibility. */
export const NOTIFICATION_CATALOG_MODULES: NotificationCatalogModule[] =
  NOTIFICATION_CATALOG_MODULE_IDS.map((id) => {
    const info = rawModules[id] ?? {};
    return {
      id,
      labelKey: info.displayNameKey ?? id,
      iconName: info.icon,
      visibility: catalogModules[id]?.visibility ?? 'all',
    };
  });

/**
 * Filter the catalog modules to those a viewer with the given role may see in
 * the central matrix. A sysadmin-tenant admin sees everything; a tenant admin
 * sees `all` + `tenant_admin`; a regular user sees only `all`.
 */
export function visibleNotificationCatalogModules(viewer: {
  isAdmin: boolean;
  isSysadmin: boolean;
}): NotificationCatalogModule[] {
  return NOTIFICATION_CATALOG_MODULES.filter((mod) => {
    switch (mod.visibility) {
      case 'sysadmin':
        return viewer.isSysadmin;
      case 'tenant_admin':
        return viewer.isAdmin || viewer.isSysadmin;
      default:
        return true;
    }
  });
}
